import React, { useMemo } from "react";
import { Autocomplete, Box, Grid, TextField } from "@mui/material";
import { Formik } from "formik";
import * as Yup from "yup";
import {
  CreateTrailerMaintenanceRecordRequest,
  TrailerMaintenanceRecordDto,
  useCreateTrailerMaintenanceRecordMutation,
  useUpdateTrailerMaintenanceRecordMutation,
} from "../../../api/maintenanceApi";
import { Trailer } from "../../../api/trailersApi";
import { useGetShopsQuery } from "../../../api/shopsApi";
import { FormDialog } from "../../../components/common/FormDialog/FormDialog";
import {
  GenericNumberField,
  GenericDateField,
  GenericTextField,
} from "../../../components/common/FormFields";
import InvoiceGalleryPicker from "./InvoiceGalleryPicker";

interface TrailerMaintenanceRecordDialogProps {
  open: boolean;
  onClose: () => void;
  trailers: Trailer[];
  preselectedTrailerId?: number | null;
  editRecord?: TrailerMaintenanceRecordDto | null;
}

const validationSchema = Yup.object({
  trailerId: Yup.number().required("Trailer is required").min(1, "Trailer is required"),
  date: Yup.string().required("Date is required"),
  description: Yup.string().required("Description is required"),
});

const toDateInputValue = (date: Date | string): string => {
  if (typeof date === "string") {
    return date.split("T")[0];
  }
  return date.toISOString().split("T")[0];
};

const trailerLabel = (t: Trailer) =>
  t.unitNumber || t.vin || `Trailer #${t.id}`;

const TrailerMaintenanceRecordDialog: React.FC<TrailerMaintenanceRecordDialogProps> = ({
  open,
  onClose,
  trailers,
  preselectedTrailerId,
  editRecord,
}) => {
  const [createRecord, { isLoading: isCreating }] =
    useCreateTrailerMaintenanceRecordMutation();
  const [updateRecord, { isLoading: isUpdating }] =
    useUpdateTrailerMaintenanceRecordMutation();
  const { data: shops = [] } = useGetShopsQuery(undefined);
  const activeShops = useMemo(() => shops.filter((s) => s.isActive), [shops]);

  const isEditMode = !!editRecord;
  const isLoading = isCreating || isUpdating;

  const initialValues: CreateTrailerMaintenanceRecordRequest & {
    invoiceIds: number[];
  } = {
    trailerId: editRecord?.trailerId ?? preselectedTrailerId ?? 0,
    shopId: editRecord?.shopId ?? null,
    cost: editRecord?.cost ?? null,
    date: editRecord ? toDateInputValue(editRecord.date) : toDateInputValue(new Date()),
    mileage: editRecord?.mileage ?? null,
    description: editRecord?.description ?? "",
    invoiceIds: editRecord?.invoices?.map((i) => i.id) ?? [],
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async (values, { resetForm }) => {
        try {
          if (isEditMode) {
            await updateRecord({
              recordId: editRecord!.id,
              data: {
                trailerId: values.trailerId,
                shopId: values.shopId,
                cost: values.cost,
                date: values.date,
                mileage: values.mileage,
                description: values.description,
                invoiceIds: values.invoiceIds.length > 0 ? values.invoiceIds : null,
              },
            }).unwrap();
          } else {
            await createRecord({
              ...values,
              invoiceIds: values.invoiceIds.length > 0 ? values.invoiceIds : null,
            }).unwrap();
          }
          resetForm();
          onClose();
        } catch (err) {
          console.error(
            `Failed to ${isEditMode ? "update" : "create"} trailer maintenance record:`,
            err
          );
        }
      }}
    >
      {({ handleSubmit: formikSubmit, values, setFieldValue, errors, touched, resetForm }) => (
        <FormDialog
          open={open}
          title={
            isEditMode
              ? "Edit Trailer Maintenance Record"
              : "Add Trailer Maintenance Record"
          }
          onClose={() => {
            resetForm();
            onClose();
          }}
          onSubmit={formikSubmit}
          isLoading={isLoading}
          maxWidth="md"
        >
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                size="small"
                options={trailers}
                getOptionLabel={trailerLabel}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                value={trailers.find((t) => t.id === values.trailerId) ?? null}
                onChange={(_, selected) => {
                  setFieldValue("trailerId", selected?.id ?? 0);
                  setFieldValue("invoiceIds", []);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Trailer *"
                    placeholder="Type to search..."
                    error={touched.trailerId && Boolean(errors.trailerId)}
                    helperText={touched.trailerId && errors.trailerId}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                size="small"
                options={activeShops}
                getOptionLabel={(s) => s.name}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                value={activeShops.find((s) => s.id === values.shopId) ?? null}
                onChange={(_, selected) => setFieldValue("shopId", selected?.id ?? null)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Shop"
                    placeholder="Type to search..."
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <GenericNumberField name="cost" label="Cost ($)" min={0} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <GenericDateField name="date" label="Date *" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <GenericNumberField name="mileage" label="Mileage (optional)" min={0} />
            </Grid>
            <Grid item xs={12}>
              <GenericTextField
                name="description"
                label="Description *"
                multiline
                rows={3}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3 }}>
            <InvoiceGalleryPicker
              selectedIds={values.invoiceIds}
              onChange={(ids) => setFieldValue("invoiceIds", ids)}
              trailerId={values.trailerId || null}
              knownInvoices={editRecord?.invoices}
            />
          </Box>
        </FormDialog>
      )}
    </Formik>
  );
};

export default TrailerMaintenanceRecordDialog;
